import React from "react";
import InputField from "../../components/Forms/InputField";
import SwitchField from "../../components/Forms/SwitchField";
import CommonDialog from "../../components/CommonDialog";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import {
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
} from "@mui/material";
import PercentIcon from "@mui/icons-material/Percent";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Axios from "../../config/axios";
import { startBlockingDialog, endBlockingDialog } from "../../Redux/loadingSlice";
import AutocompleteInput from "../../components/Forms/AutocompleteInput";

const RepaymentTypeList = [
  {
    name: "MONTHLY",
    id: "MONTHLY",
  },
  {
    name: "WEEKLY",
    id: "WEEKLY",
  },
  {
    name: "DAILY",
    id: "DAILY",
  },
];

// ** YUP validation
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  mandatoryLending: Yup.boolean(),
  maximumLoanAmount: Yup.number(),
  originationFeeAllowed: Yup.boolean(),
  originationChargeType: Yup.string().oneOf(["percentage", "dollar"]).nullable(),
  originationFeePercentage: Yup.number().when(["originationFeeAllowed", "originationChargeType"], {
    is: (allowed, type) => allowed && type === "percentage",
    then: (schema) =>
      schema
        .typeError("Origination Fee Percentage must be a number")
        .min(0, "Must be zero or a positive number")
        .max(100, "Cannot exceed 100")
        .required("Origination Fee Required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),

  originationFeeDollar: Yup.number().when(["originationFeeAllowed", "originationChargeType"], {
    is: (allowed, type) => allowed && type === "dollar",
    then: (schema) =>
      schema
        .typeError("Origination Fee Dollar must be a number")
        .min(0, "Must be zero or a positive number")
        .required("Origination Fee Dollar Required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),

  originationMaxDollarAmount: Yup.number().when(["originationFeeAllowed", "originationChargeType"], {
    is: (allowed, type) => allowed && type === "percentage",
    then: (schema) =>
      schema
        .typeError("Origination Max Dollar Amount must be a number")
        .min(0, "Must be zero or a positive number")
        .required("Origination Max Dollar Amount Required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  prepaymentFeeAllowed: Yup.boolean(),
  prepaymentChargeType: Yup.string().oneOf(["percentage", "dollar"]).nullable(),
  prepaymentFeePercentage: Yup.number().when(["prepaymentFeeAllowed", "prepaymentChargeType"], {
    is: (allowed, type) => allowed && type === "percentage",
    then: (schema) =>
      schema
        .typeError("Prepayment Fee Percentage must be a number")
        .min(0, "Must be zero or a positive number")
        .max(100, "Cannot exceed 100")
        .required("Prepayment Fee Required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  prepaymentFeeDollar: Yup.number().when(["prepaymentFeeAllowed", "prepaymentChargeType"], {
    is: (allowed, type) => allowed && type === "dollar",
    then: (schema) =>
      schema
    .typeError("Prepayment Fee Dollar must be a number")
        .min(0, "Must be zero or a positive number")
        .max(100, "Cannot exceed 100")
        .required("Prepayment Fee Dollar is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),

  prepaymentMaxDollarAmount: Yup.number().when(["prepaymentFeeAllowed", "prepaymentChargeType"], {
    is: (allowed, type) => allowed && type === "percentage",
    then: (schema) =>
      schema
    .typeError("Pre Payment Max Dollar Amount must be a number")
        .min(0, "Must be zero or a positive number")
        .max(100, "Cannot exceed 100")
        .required("Pre Payment Max Dollar Amount is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),

  maximumPrepaymentFeeAllowed: Yup.boolean(),

  maximumPrepaymentFee: Yup.number().when("maximumPrepaymentFeeAllowed", {
    is: true,
    then: Yup.number()
      .typeError("Maximum Prepayment Fee must be a number")
      .min(0, "Must be zero or a positive number")
      .required("Maximum Prepayment Fee is required"),
    otherwise: Yup.number().nullable().notRequired(),
  }),
  loanDuration: Yup.number()
    .typeError("Loan Duration must be a number")
    .min(1, "Must be at least 1")
    .required("Loan Duration Required"),
  loanFrequency: Yup.object()
      .required("Please select loan frequency")
      .typeError("Please select loan frequency"),
  serviceChargeType: Yup.string()
    .oneOf(["fees", "apr_percentage"], "Invalid Service Charge Type")
    .required("Loan frequency Required"),
  unitFee: Yup.number().when("serviceChargeType", {
    is: "fees",
    then: Yup.number()
      .typeError("Unit Fee must be a number")
      .min(0, "Must be zero or a positive number")
      .required("Unit Fee Required"),
  }),
  aprPer: Yup.number().when("serviceChargeType", {
    is: "apr_percentage",
    then: Yup.number()
      .typeError("APR % must be a number")
      .min(0, "Must be zero or a positive number")
      .required("APR % Required"),
  })
});

function AddLoanProfile({ open, onClose, getProfileList }) {
  const dispatch = useDispatch();

  // ** React hook form
  const { control, handleSubmit, watch } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      default: false,
      originationFeeAllowed: false,
      prepaymentFeeAllowed: false,
      loanFrequency: {
        name: "WEEKLY",
        id: "WEEKLY",
      },
      loanDuration: 10,
      serviceChargeType: "fees",
      originationChargeType: "dollar",
      prepaymentChargeType: "dollar",
    },
  });

  const originationFeeAllowed = watch("originationFeeAllowed");
  const prepaymentFeeAllowed = watch("prepaymentFeeAllowed");
  const serviceChargeType = watch("serviceChargeType");
  const originationChargeType = watch("originationChargeType");
  const prepaymentChargeType = watch("prepaymentChargeType");

  // ** API service for submit form
  const onSubmit = async (data) => {
    console.log("Form Data", data);
    try {
      dispatch(startBlockingDialog());
      data.loanFrequency = data.loanFrequency.id;
      const response = await Axios.post("/loan-profile/admin", data);

      if (response?.data?.success) {
        toast.success(response?.data?.message);
        onClose();
        getProfileList();
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(endBlockingDialog());
    }
  };

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title="Add Loan Profile"
      handleSubmit={() => handleSubmit(onSubmit)()} 
    >
      <Grid container spacing={2}>
        {/* Profile Name */}
        <Grid item sm={12} md={6}>
          <InputField
            control={control}
            name="name"
            label="Profile Name"
            placeholder="Enter profile name"
          />
        </Grid>

        {/* Origination Fee */}
        <Grid item xs={12}>
          <SwitchField
            control={control}
            name="originationFeeAllowed"
            label="Origination Fee"
          />
        </Grid>

        {originationFeeAllowed && (
          <>
            <Grid item sm={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel sx={{ color: "white !important" }}>
                Origination Fee Type
                </FormLabel>
                <Controller
                  name="originationChargeType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ mt: -0.5, ml: 1 }}>
                      <FormControlLabel
                        value="dollar"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Fixed Dollar ($)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                      <FormControlLabel
                        value="percentage"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Percentage (%)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>

            {originationChargeType === "percentage" && (
              <Grid item sm={12} md={6}>
                <InputField
                  control={control}
                  name="originationFeePercentage"
                  label="Origination Fee (%)"
                  type="number"
                  isPercentages={true}
                  placeholder="Enter APR percentage"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" sx={{ color: "#918e8e" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <InputField
                  control={control}
                  name="originationMaxDollarAmount"
                  label="Maximum Origination Fee"
                  type="number"
                  isPercentages={true}
                  placeholder="Enter Maximum Origination Fee"
                  InputProps={{
                    endAdornment: ( 
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" sx={{ color: "#918e8e" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            {originationChargeType === "dollar" && (
              <Grid item sm={12} md={6}>
                <InputField
                  control={control}
                  name="originationFeeDollar"
                  label="Origination Fee ($)"
                  type="number"
                  isCurrency={true}
                  placeholder="Enter fee amount"
                />
              </Grid>
            )}
          </>
        )}

        {/* Prepayment Fee */}
        <Grid item xs={12}>
          <SwitchField
            control={control}
            name="prepaymentFeeAllowed"
            label="Prepayment Fee"
          />
        </Grid>

        {prepaymentFeeAllowed && (
          <>
            <Grid item sm={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel sx={{ color: "white !important" }}>
                Prepayment Fee Type
                </FormLabel>
                <Controller
                  name="prepaymentChargeType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ mt: -0.5, ml: 1 }}>
                      <FormControlLabel
                        value="dollar"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Fixed Dollar ($)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                      <FormControlLabel
                        value="percentage"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Percentage (%)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>

            {prepaymentChargeType === "percentage" && (
              <Grid item sm={12} md={6}>
                <InputField
                  control={control}
                  name="prepaymentFeePercentage"
                  label="Prepayment Fee (%)"
                  type="number"
                  isPercentages={true}
                  placeholder="Enter APR percentage"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" sx={{ color: "#918e8e" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <InputField
                  control={control}
                  name="prepaymentMaxDollarAmount"
                  label="Maximum Prepayment Fee"
                  type="number"
                  isPercentages={true}
                  placeholder="Enter Max APR percentage"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" sx={{ color: "#918e8e" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            {prepaymentChargeType === "dollar" && (
              <Grid item sm={12} md={6}>
                <InputField
                  control={control}
                  name="prepaymentFeeDollar"
                  label="Prepayment Fee ($)"
                  type="number"
                  isCurrency={true}
                  placeholder="Enter fee amount"
                />
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12}>
          <SwitchField
            control={control}
            name="mandatoryLending"
            label="Is Mandatory Lending?"
          />
        </Grid>

        {/* Maximum Loan Amount */}
        <Grid item xs={12}>
          <InputField
            control={control}
            name="maximumLoanAmount"
            label="Maximum Loan Amount ($)"
            type="number"
          />
        </Grid>

        {/* Loan Frequency */}
        <Grid item xs={6}>
          <AutocompleteInput
            control={control}
            options={RepaymentTypeList}
            name="loanFrequency"
            label="Loan Repayment Frequency"
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

         {/* Loan Duration */}
         <Grid item xs={6}>
          <InputField
            control={control}
            name="loanDuration"
            label="Loan Installment"
            type="number"
          />
        </Grid>

        {/* Service Charge Type */}
        <Grid item xs={6}>
          <FormControl component="fieldset">
            <FormLabel sx={{ color: "white !important" }}>Service Charge Type</FormLabel>
            <Controller
              name="serviceChargeType"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value="fees"
                    control={<Radio />}
                    label="Fees for every $10"
                    sx={{
                      color: "white",
                      "& .MuiTypography-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                      "& .MuiFormControlLabel-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                    }}
                  />
                  <FormControlLabel
                    value="apr_percentage"
                    control={<Radio />}
                    label="APR %"
                    sx={{
                      color: "white",
                      "& .MuiTypography-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                      "& .MuiFormControlLabel-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                    }}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Grid>

        {serviceChargeType === "fees" && (
          <Grid item xs={6}>
            <InputField
              control={control}
              name="unitFee"
              label="Fees for every $10"
              type="number"
            />
          </Grid>
        )}

        {serviceChargeType === "apr_percentage" && (
          <Grid item xs={6}>
            <InputField
              control={control}
              name="aprPer"
              label="APR %"
              type="number"
            />
          </Grid>
        )}
      </Grid>
    </CommonDialog>
  );
}

export default AddLoanProfile;
